/* Copyright Levelise Ltd 2019-2026 */
import config from '../config';
import Cookies from 'universal-cookie';
import IdleService from './idle-service';
import UserService from './user-service';
import { createReqBody } from '../utils/utils';
import OAuthService from './auth-service';

let _timeoutId;
const _ms_before_expiry = 10 * 1000;
const cookies = new Cookies();

let tokenRefreshProcess = null;

const TokenService = {
	saveState(state) {
		sessionStorage.setItem(config.state, state);
	},

	getState() {
		return sessionStorage.getItem(config.state);
	},

	saveCodeVerifier(codeVerifier) {
		sessionStorage.setItem(config.codeVerifier, codeVerifier);
	},

	getCodeVerifier() {
		return sessionStorage.getItem(config.codeVerifier);
	},

	saveAuthToken(token) {
		cookies.set(config.Auth_Token, JSON.stringify(token), { path: '/' });
	},

	getAuthToken() {
		return cookies.get(config.Auth_Token);
	},

	clearAuthToken() {
		TokenService.clearCallbackBeforeExpiry();
		cookies.remove(config.Auth_Token);
	},

	hasAuthToken() {
		return !!cookies.get(config.Auth_Token);
	},

	makeBasicAuthToken(userName, password) {
		return window.btoa(`${userName}:${password}`);
	},

	async getDataFromAuthToken(type) {
		if (tokenRefreshProcess) {
			await tokenRefreshProcess;
		}

		if (TokenService.hasAuthToken()) return TokenService.getAuthToken()[type];
		else TokenService.logoutFromTokenError();
	},

	logoutFromTokenError() {
		window.location.href = '/';
		TokenService.clearAuthToken();
		UserService.removeUser();
		UserService.removeUserDetail();
		IdleService.unRegisterIdleResets();
	},

	queueCallbackBeforeExpiry(callback, expires_in) {
		const ms_until_expiry = parseInt(expires_in) * 1000;
		let ms_until_cb = ms_until_expiry - _ms_before_expiry; // 10 sec before expiry
		
		if (ms_until_cb < _ms_before_expiry) {
			ms_until_cb = 5000; // 5 sec minimum
		}

		clearTimeout(_timeoutId);
		_timeoutId = setTimeout(callback, ms_until_cb);
	},

	clearCallbackBeforeExpiry() {
		clearTimeout(_timeoutId);
	},

	getTimeoutId() {
		return _timeoutId;
	},

	async refreshToken() {
		try {
			const data = {
				grant_type: 'refresh_token',
				refresh_token: await TokenService.getDataFromAuthToken('refresh_token'),
				scope: 'write',
			};

			const basicToken = TokenService.makeBasicAuthToken(
				// eslint-disable-next-line no-undef
				process.env.REACT_APP_CLIENT_ID,
				// eslint-disable-next-line no-undef
				process.env.REACT_APP_SECRET,
			);

			if (!tokenRefreshProcess) {
				tokenRefreshProcess = fetch(`${config.REST_URI}/oauth/token`, {
					method: 'POST',
					headers: {
						Authorization: `Basic ${basicToken}`,
						'Content-Type': 'application/x-www-form-urlencoded',
					},
					body: createReqBody(data),
				})
					.then(async (res) => {
						const data = await res.json();
						if (!res.ok) {
							return Promise.reject(data);
						}
						return data;
					})
					.then(async (authData) => {
						await OAuthService.saveToken(authData);

						TokenService.queueCallbackBeforeExpiry(
							TokenService.refreshToken,
							authData.expires_in,
						);
					})
					.catch((err) => {
						console.log(err);
						TokenService.clearAuthToken();
						UserService.removeUser();
						UserService.removeUserDetail();
						TokenService.clearCallbackBeforeExpiry();
						IdleService.unRegisterIdleResets();
						window.location.href = '/';
					})
					.finally(() => {
						tokenRefreshProcess = null;
					});
			}
		} catch {
			console.log('Refresh failed!');
		}
	},
};

export default TokenService;
